import { createBrowserRouter, Navigate } from "react-router-dom";

import PublicLayout from "../layouts/PublicLayout";
import AccountLayout from "../layouts/AccountLayout";
import CricketLayout from "../layouts/CricketLayout";
import BallLayout from "../layouts/BallLayout";
import AdminLayout from "../layouts/AdminLayout";
import RequireSuperuser from "../components/common/RequireSuperuser";

/* ===== OWNER ADMIN (B2C admin at /admin/*) ===== */
import AdminDashboard     from "../pages/admin-app/Dashboard";
import AdminCreate        from "../pages/admin-app/Create";
import AdminUsers         from "../pages/admin-app/Users";
import AdminOrganizations from "../pages/admin-app/Organizations";
import AdminMarketplace   from "../pages/admin-app/Marketplace";
import AdminFinances      from "../pages/admin-app/Finances";
import AdminSpend         from "../pages/admin-app/Spend";
import AdminBilling       from "../pages/admin-app/Billing";
// NB: named AdminRefunds — `Refunds` is already the CUSTOMER page at
// /account/billing/refunds. Two different screens, similar names.
import AdminRefunds       from "../pages/admin-app/Refunds";
import AdminActivity      from "../pages/admin-app/Activity";
import AdminIngestReview from "../pages/admin-app/IngestReview";
import AdminIngestAnomalies from "../pages/admin-app/IngestAnomalies";
import AdminIngestCoverage from "../pages/admin-app/IngestCoverage";
import AdminIngestPlayers from "../pages/admin-app/IngestPlayers";
import AdminZcdConvert from "../pages/admin-app/ZcdConvert";
import LeadsList from "../pages/admin-app/leads/LeadsList";
import AddLead from "../pages/admin-app/leads/AddLead";
import CsvImportLeads from "../pages/admin-app/leads/CsvImport";
import AdminPlayerBulkUpload from "../pages/admin-app/PlayerBulkUpload";
import AdminAILearning from "../pages/admin-app/AILearning";
import AdminAIUsage from "../pages/admin-app/AIUsage";
import AdminFeedback from "../pages/admin-app/FeedbackAdmin";
import AdminDatabase from "../pages/admin-app/Database";
import AdminDataExport from "../pages/admin-app/DataExport";
import AdminMatchVideos from "../pages/admin-app/MatchVideos";
import AdminVideoSlicingStatus from "../pages/admin-app/VideoSlicingStatus";
import AdminVideoTagging from "../pages/admin-app/VideoTagging";
import AdminClipAssignments from "../pages/admin-app/ClipAssignments";
import AdminMatchIncidents from "../pages/admin-app/MatchIncidents";
import AdminMatches from "../pages/admin-app/Matches";
import AdminTeams from "../pages/admin-app/Teams";
import AdminVenueSync from "../pages/admin-app/VenueSync";
import AdminScorerMarketplace from "../pages/admin-app/ScorerMarketplace";
import AdminAccess from "../pages/admin-app/Access";
import OrgOnboarding      from "../pages/onboarding/OrgOnboarding";

import RootGate from "../pages/public/RootGate";
import Subscriptions from "../pages/account/Subscriptions";
import Refunds from "../pages/account/Refunds";
import Portfolio from "../pages/account/Portfolio";
import MyPlayerProfile from "../pages/account/PlayerProfile";
import MyPlayerHub from "../pages/account/MyPlayerHub";
import FindAScorer from "../pages/public/FindAScorer";
import ScorerDetail from "../pages/public/ScorerDetail";
import FindAPlayer from "../pages/public/FindAPlayer";
import PlayerDetail from "../pages/public/PlayerDetail";
import ClaimPlayer from "../pages/public/ClaimPlayer";
import OrganisationDashboard from "../components/organisation/OrganisationDashboard";

import Login from "../pages/public/Login";
import Signup from "../pages/public/Signup";
import MandateReturn from "../pages/public/MandateReturn";
import Maintenance from "../pages/public/Maintenance";
import CheckYourEmail from "../pages/public/CheckYourEmail";
import VerifyEmail from "../pages/public/VerifyEmail";
import ForgotPassword from "../pages/public/ForgotPassword";
import ResetPassword from "../pages/public/ResetPassword";
import Dashboard from "../pages/account/Dashboard";
import AcceptInvite from "../pages/public/AcceptInvite";
import ContactSales from "../pages/public/ContactSales";
import Careers from "../pages/public/Careers";


/* ===== LIST PAGES ===== */
import MatchesList from "../pages/cricket/matches/List";
import CompetitionsList from "../pages/cricket/competitions/List";
import TournamentsList from "../pages/cricket/tournaments/List";
import TeamsList from "../pages/cricket/teams/List";
import PlayersList from "../pages/cricket/players/List";
import UmpiresList from "../pages/cricket/umpires/List";
import StadiumsList from "../pages/cricket/stadiums/List";

/* ===== ADD PAGES ===== */
import AddMatch from "../pages/cricket/matches/Add";
import AddCompetition from "../pages/cricket/competitions/Add";
import AddTournament from "../pages/cricket/tournaments/Add";
import AddTeam from "../pages/cricket/teams/Add";
import AddPlayer from "../pages/cricket/players/Add";
import AddUmpire from "../pages/cricket/umpires/Add";
import AddStadium from "../pages/cricket/stadiums/Add";

/* ===== EDIT PAGES ===== */
import MatchControl from "../pages/cricket/matches/MatchControl";
import ApiTokens from "../pages/account/ApiTokens";
import RequireOrg from "../components/common/RequireOrg";
import Usage from "../pages/account/Usage";
import Dedupe from "../pages/admin/Dedupe";
import DisputedBalls from "../pages/admin/DisputedBalls";
import VoiceAnalytics from "../pages/admin/VoiceAnalytics";
import Chyron from "../pages/overlay/Chyron";
import MatchHealth from "../pages/admin/MatchHealth";
import PublicPlayerProfile from "../pages/cricket/players/Profile";
import EditMatch from "../pages/cricket/matches/Edit";
import EditCompetition from "../pages/cricket/competitions/Edit";
import CompetitionDetail from "../pages/cricket/competitions/Detail";
import CompetitionSync, { MatchSync, TournamentSync } from "../pages/cricket/competitions/Sync";
import TournamentDetail from "../pages/cricket/tournaments/Detail";
import EditTournament from "../pages/cricket/tournaments/Edit";
import EditTeam from "../pages/cricket/teams/Edit";
import EditPlayer from "../pages/cricket/players/Edit";
import EditUmpire from "../pages/cricket/umpires/Edit";
import EditStadium from "../pages/cricket/stadiums/Edit";

/* ===== SELECTION PAGES ===== */
import CompetitionTeamSelection from "../pages/selections/CompetitionTeamSelection";
import CompetitionSquadSelection from "../pages/selections/CompetitionSquadSelection";

/* ===== MATCH PREP PAGES ===== */
import Toss from "../pages/cricket/matches/Toss";
import PlayingXI from "../pages/cricket/matches/PlayingXI";
import Squad from "../pages/cricket/matches/Squad";

/* ===== SCORECARD ===== */
import Scorecard from "../pages/cricket/matches/Scorecard";
import EditBalls from "../pages/cricket/matches/EditBalls";


import ConfirmMatch from "../pages/ballbyball/ConfirmMatch";
import BallByBallPage from "../pages/ballbyball/BallByBallPage";
import BallQCPage from "../pages/ballbyball/BallQCPage";
import BallEntriesPage from "../pages/ballbyball/BallEntriesPage";
import { BallInputProvider } from "../context/BallInputContext";

/* ===== INGEST ===== */
import IngestJobsList from "../pages/ingest/JobsList";
import NewIngestJob from "../pages/ingest/NewJob";
import IngestJobDetail from "../pages/ingest/JobDetail";
import RequireIngest from "../components/common/RequireIngest";

/* ===== WALLET / BILLING ===== */
import Wallet from "../pages/account/Wallet";
import Topup from "../pages/account/Topup";
import Invoices from "../pages/account/Invoices";
import StorageManager from "../pages/account/StorageManager";
import Privacy from "../pages/account/Privacy";
import Marketplace from "../pages/account/Marketplace";

/* ===== HELP ===== */
import ScorerGuide from "../pages/help/ScorerGuide";
import AIGuide from "../pages/help/AIGuide";
import UserGuide from "../pages/help/UserGuide";

const router = createBrowserRouter([
  /* ===== PUBLIC ===== */
  {
    element: <PublicLayout />,
    children: [
      { path: "/", element: <RootGate /> },
      { path: "/login", element: <Login /> },
      // Signups re-enabled 2026-06-10 (founder reversal). Backend env
      // SIGNUPS_ENABLED=true was flipped at the same time.
      { path: "/signup", element: <Signup /> },
      // Trial-mandate (2026-06-22, Track A) — Cashfree redirects back
      // here after the customer authorises the mandate. Page polls
      // /api/subscriptions/trial/confirm/<sub_id>/ until the trial
      // flips PENDING → ACTIVE.
      { path: "/signup/mandate-return", element: <MandateReturn /> },
      // Pre-launch maintenance page — STANDBY accounts land here after
      // login, gated by AccountLayout / BallLayout (see layouts).
      { path: "/maintenance", element: <Maintenance /> },
      { path: "/check-your-email", element: <CheckYourEmail /> },
      { path: "/verify-email", element: <VerifyEmail /> },
      { path: "/forgot-password", element: <ForgotPassword /> },
      { path: "/reset-password/:uidb64/:token", element: <ResetPassword /> },
      { path: "/accept-invite/:token", element: <AcceptInvite /> },
      { path: "/contact-sales", element: <ContactSales /> },
      // 2026-08-26 — public careers page (general application, no roles list)
      { path: "/careers", element: <Careers /> },
      // T247.G — public scorer-for-hire marketplace
      { path: "/find-a-scorer", element: <FindAScorer /> },
      { path: "/find-a-scorer/:id", element: <ScorerDetail /> },
      // T264 — public Find-a-Player marketplace
      { path: "/find-a-player", element: <FindAPlayer /> },
      { path: "/find-a-player/:id", element: <PlayerDetail /> },
      // T265 — Quick Player claim landing
      { path: "/claim/:token", element: <ClaimPlayer /> },
      // Compat with old marketing-SPA paths
      { path: "/sign-in", element: <Navigate to="/login" replace /> },
      { path: "/register", element: <Navigate to="/signup" replace /> },
      { path: "/admin-portal", element: <Navigate to="/organisation-dashboard" replace /> },
      { path: "/score", element: <Navigate to="/cricket/matches" replace /> },
    ],
  },

  /* ===== ACCOUNT ===== */
  {
    element: <AccountLayout />,
    children: [
      { path: "/dashboard", element: <Dashboard /> },
      { path: "/subscriptions", element: <Subscriptions /> },
      // T223.D — customer refund submission + history
      { path: "/account/billing/refunds", element: <Refunds /> },
      // T247.F — scorer marketplace portfolio editor (Elite perk)
      { path: "/account/portfolio", element: <Portfolio /> },
      // T265 — player profile editor
      { path: "/account/player-profile", element: <MyPlayerProfile /> },
      // 2026-05-30 — Player self-signup landing (profile + stats hub)
      { path: "/me/player", element: <MyPlayerHub /> },
      { path: "/organisation-dashboard", element: <OrganisationDashboard /> },
      // B2B post-signup onboarding wizard. Stub today (G4); full
      // 4-step wizard ships in G5. Auth-gated via the same AccountLayout
      // wrapper as /dashboard.
      { path: "/onboarding/org", element: <OrgOnboarding /> },
      /* Data ingestion (org-only — backend permission + RequireIngest route
         guard + per-page is_ingest_allowed check). B2C users redirect to
         /dashboard silently. */
      { path: "/ingest",                element: <RequireIngest><IngestJobsList /></RequireIngest> },
      { path: "/ingest/new",            element: <RequireIngest><NewIngestJob /></RequireIngest> },
      { path: "/ingest/jobs/:jobId",    element: <RequireIngest><IngestJobDetail /></RequireIngest> },
      /* Wallet / credits */
      { path: "/account/wallet",        element: <Wallet /> },
      { path: "/account/topup",         element: <Topup /> },
      { path: "/account/invoices",      element: <Invoices /> },
      { path: "/account/storage",       element: <StorageManager /> },
      // DPDP — data export + account deletion (Right to Access + Erasure).
      { path: "/account/privacy",       element: <Privacy /> },
      /* Marketplace — admin-curated player/team packs (clone-on-purchase). */
      { path: "/marketplace",           element: <Marketplace /> },
      /* Help / training */
      { path: "/help/scorer-guide",     element: <ScorerGuide /> },
      { path: "/help/ai-guide",         element: <AIGuide /> },
      { path: "/help/user-guide",       element: <UserGuide /> },
    ],
  },

  /* ===== CRICKET ===== */
  {
    path: "/cricket",
    element: <CricketLayout />,
    children: [
      // Default landing inside /cricket → matches list
      { index: true, element: <Navigate to="/cricket/matches" replace /> },
      /* LIST */
      { path: "matches", element: <MatchesList /> },
      { path: "competitions", element: <CompetitionsList /> },
      { path: "tournaments", element: <TournamentsList /> },
      { path: "teams", element: <TeamsList /> },
      { path: "players", element: <PlayersList /> },
      { path: "umpires", element: <UmpiresList /> },
      { path: "stadiums", element: <StadiumsList /> },

      /* ADD */
      { path: "matches/add", element: <AddMatch /> },
      { path: "competitions/add", element: <AddCompetition /> },
      { path: "tournaments/add", element: <AddTournament /> },
      { path: "teams/add", element: <AddTeam /> },
      { path: "players/add", element: <AddPlayer /> },
      { path: "umpires/add", element: <AddUmpire /> },
      { path: "stadiums/add", element: <AddStadium /> },

      /* EDIT */
      { path: "matches/:id/edit", element: <EditMatch /> },
      { path: "matches/:id/sync", element: <MatchSync /> },
      { path: "competitions/:id", element: <CompetitionDetail /> },
      { path: "competitions/:id/sync", element: <CompetitionSync /> },
      { path: "competitions/tournament-sync", element: <TournamentSync /> },
      { path: "tournaments/:id", element: <TournamentDetail /> },
      { path: "competitions/:id/edit", element: <EditCompetition /> },
      { path: "tournaments/:id/edit", element: <EditTournament /> },
      { path: "teams/:id/edit", element: <EditTeam /> },
      { path: "players/:id/edit", element: <EditPlayer /> },
      { path: "umpires/:id/edit", element: <EditUmpire /> },
      { path: "stadiums/:id/edit", element: <EditStadium /> },

      /* ===== SELECTION ===== */

      /* Competition */
      {
        path: "competitions/:id/teams",
        element: <CompetitionTeamSelection />,
      },
      {
        path: "competitions/:id/squads",
        element: <CompetitionSquadSelection />,
      },

      /* ===== MATCH PREPARATION ===== */

      {
        path: "matches/:id/toss",
        element: <Toss />,
      },
      {
        path: "matches/:id/squad",
        element: <Squad />,
      },
      {
        path: "matches/:id/playing-xi",
        element: <PlayingXI />,
      },
      {
        path: "matches/:id/confirm-scoring",
        element: <ConfirmMatch />,
      },
      {
        path: "matches/:id/scorecard",
        element: <Scorecard />,
      },
      {
        path: "matches/:id/edit-balls",
        element: <EditBalls />,
      },
      {
        path: "matches/:id/control",
        element: <MatchControl />,
      },
      {
        // Org-only feature; B2C users redirect to /dashboard via RequireOrg
        // before the page renders. Backend permission gate is the real
        // protection — this is defence-in-depth.
        path: "api-tokens",
        element: <RequireOrg><ApiTokens /></RequireOrg>,
      },
      {
        path: "usage",
        element: <RequireOrg><Usage /></RequireOrg>,
      },
      {
        path: "dedupe",
        element: <Dedupe />,
      },
      {
        path: "disputed-balls",
        element: <DisputedBalls />,
      },
      {
        path: "voice-analytics",
        element: <VoiceAnalytics />,
      },
      {
        path: "players/:id/profile",
        element: <PublicPlayerProfile />,
      },
    ],
  },
  /* ===== OWNER ADMIN ===== */
  // Superuser-only B2C admin. Gate is defense-in-depth — backend enforces
  // is_superuser on every /api/admin/v2/* endpoint. Non-superusers who
  // guess the URL get bounced to /dashboard before the admin chrome renders.
  {
    path: "/admin",
    element: (
      <RequireSuperuser>
        <AdminLayout />
      </RequireSuperuser>
    ),
    children: [
      { index: true,            element: <AdminDashboard /> },
      { path: "create",         element: <AdminCreate /> },
      { path: "users",          element: <AdminUsers /> },
      { path: "matches",        element: <AdminMatches /> },
      { path: "teams",          element: <AdminTeams /> },
      { path: "venue-sync",     element: <AdminVenueSync /> },
      { path: "organizations",  element: <AdminOrganizations /> },
      { path: "access",         element: <AdminAccess /> },
      { path: "marketplace",    element: <AdminMarketplace /> },
      { path: "scorer-marketplace", element: <AdminScorerMarketplace /> },
      { path: "finances",       element: <AdminFinances /> },
      { path: "spend",          element: <AdminSpend /> },
      { path: "billing",        element: <AdminBilling /> },
      { path: "refunds",        element: <AdminRefunds /> },
      { path: "activity",       element: <AdminActivity /> },
      { path: "ingest/review",       element: <AdminIngestReview /> },
      { path: "ingest/anomalies",    element: <AdminIngestAnomalies /> },
      { path: "ingest/coverage",     element: <AdminIngestCoverage /> },
      { path: "ingest/players",      element: <AdminIngestPlayers /> },
      // 2026-07-09 — ZCD conversion tab (Opta CSV / Vendor XLSX → one ZCD
      // workbook per match + downloadable ZIP). Mirrors the standalone
      // Zen Ingest desktop tool.
      { path: "zcd-convert",         element: <AdminZcdConvert /> },
      { path: "leads",               element: <LeadsList /> },
      { path: "leads/add",           element: <AddLead /> },
      { path: "leads/import",        element: <CsvImportLeads /> },
      { path: "players/bulk-upload", element: <AdminPlayerBulkUpload /> },
      { path: "ai-learning",         element: <AdminAILearning /> },
      { path: "ai-usage",            element: <AdminAIUsage /> },
      { path: "feedback",            element: <AdminFeedback /> },
      { path: "match-health/:matchId", element: <MatchHealth /> },
      { path: "database",            element: <AdminDatabase /> },
      { path: "data-export",         element: <AdminDataExport /> },
      { path: "matches/:matchId/videos", element: <AdminMatchVideos /> },
      { path: "matches/:matchId/video-tagging", element: <AdminVideoTagging /> },
      { path: "matches/:matchId/video-slicing", element: <AdminVideoSlicingStatus /> },
      // Outsourced ball clipping: hand work out, review it back.
      { path: "clip-assignments",    element: <AdminClipAssignments /> },
      { path: "matches/:matchId/incidents",     element: <AdminMatchIncidents /> },
    ],
  },

  {
    path: "/ball",
    element: <BallLayout />,
    children: [
      // Default landing inside /ball → matches list (scoring entry point lives there)
      { index: true, element: <Navigate to="/cricket/matches" replace /> },
      {
        path: "scoring/:id",
        element: (
          <BallInputProvider>
            <BallByBallPage />
          </BallInputProvider>
        ),
      },
      {
        path: "entries/:id",
        element: <BallEntriesPage />,
      },
      {
        path: "qc/:id",
        element: <BallQCPage />,
      },
    ],
  },

  // Standalone broadcast overlay — no layout chrome (transparent
  // background so OBS browser-source can chroma-key over the video).
  // T131: live transcript chyron at /overlay/chyron?match=<id>
  { path: "/overlay/chyron", element: <Chyron /> },

  // Catchall — anything we don't recognise sends the user back to the root
  // (RootGate then routes them to /login or /dashboard based on auth state).
  { path: "*", element: <Navigate to="/" replace /> },
]);

export default router;
